import React from 'react';
import { Circle, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/** Sheave RPM — driver+driven with belt, motor box, fan symbol, rotation arrows. */
export function DiagramSheaveRPM() {
  const x1 = 100, y1 = 130, r1 = 32;
  const x2 = 260, y2 = 130, r2 = 22;

  return (
    <DiagramCanvas>
      {/* Motor box behind driver */}
      <Rect x={x1 - 34} y={y1 - 22} width={20} height={44} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.4" />
      <SvgText x={x1 - 24} y={y1 + 4} fill={DT.desc} fontSize="7" fontFamily={DT.mono} textAnchor="middle">MTR</SvgText>
      <Line x1={x1 - 14} y1={y1} x2={x1} y2={y1} stroke={DT.stroke} strokeWidth="2.5" />

      {/* Fan body behind driven (cylindrical housing) */}
      <Rect x={x2 + 14} y={y2 - 30} width={36} height={60} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.4" rx="3" />
      <Circle cx={x2 + 32} cy={y2} r={14} fill="none" stroke={DT.stroke} strokeWidth="1" />
      {[0, 90, 180, 270].map(deg => {
        const rad = deg * Math.PI / 180;
        return <Line key={deg} x1={x2 + 32} y1={y2}
          x2={x2 + 32 + 12 * Math.cos(rad + 0.4)} y2={y2 + 12 * Math.sin(rad + 0.4)}
          stroke={DT.stroke} strokeWidth="1" />;
      })}

      {/* Belt */}
      <Line x1={x1} y1={y1 - r1} x2={x2} y2={y2 - r2} stroke={DT.varColor} strokeWidth="3" />
      <Line x1={x1} y1={y1 + r1} x2={x2} y2={y2 + r2} stroke={DT.varColor} strokeWidth="3" />
      <Path d={`M${x1},${y1 - r1} A${r1},${r1} 0 0 0 ${x1},${y1 + r1}`}
        fill="none" stroke={DT.varColor} strokeWidth="3" />
      <Path d={`M${x2},${y2 - r2} A${r2},${r2} 0 0 1 ${x2},${y2 + r2}`}
        fill="none" stroke={DT.varColor} strokeWidth="3" />

      {/* Sheaves */}
      <Circle cx={x1} cy={y1} r={r1} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      <Circle cx={x1} cy={y1} r={4} fill={DT.stroke} />
      <Circle cx={x2} cy={y2} r={r2} fill={DT.fill} stroke={DT.stroke} strokeWidth="1.8" />
      <Circle cx={x2} cy={y2} r={3} fill={DT.stroke} />

      {/* PD dimension lines */}
      <Line x1={x1 - r1} y1={y1} x2={x1 + r1} y2={y1} stroke={DT.dim} strokeWidth="0.8" strokeDasharray="2,2" />
      <SvgText x={x1} y={y1 - 4} fill={DT.dim} fontSize="8" fontFamily={DT.mono} textAnchor="middle">PD₁</SvgText>
      <Line x1={x2 - r2} y1={y2} x2={x2 + r2} y2={y2} stroke={DT.dim} strokeWidth="0.8" strokeDasharray="2,2" />
      <SvgText x={x2} y={y2 - 4} fill={DT.dim} fontSize="8" fontFamily={DT.mono} textAnchor="middle">PD₂</SvgText>

      {/* Rotation arrows on each sheave */}
      <Path d={`M${x1 + r1 - 6},${y1 - 14} A14,14 0 0 1 ${x1 + r1 + 4},${y1 + 4}`} fill="none" stroke={DT.leader} strokeWidth="1.2" />
      <Polygon points={`${x1 + r1 + 4},${y1 + 4} ${x1 + r1 + 8},${y1 - 1} ${x1 + r1},${y1 + 0}`} fill={DT.leader} />
      <SvgText x={x1 + r1 + 16} y={y1 + 4} fill={DT.leader} fontSize="9" fontFamily={DT.mono} fontWeight="700">N₁</SvgText>

      <Path d={`M${x2 + r2 - 4},${y2 - 10} A10,10 0 0 1 ${x2 + r2 + 2},${y2 + 4}`} fill="none" stroke={DT.varColor} strokeWidth="1.2" />
      <Polygon points={`${x2 + r2 + 2},${y2 + 4} ${x2 + r2 + 6},${y2 - 1} ${x2 + r2 - 2},${y2}`} fill={DT.varColor} />
      <SvgText x={x2 + r2 + 12} y={y2 + 4} fill={DT.varColor} fontSize="9" fontFamily={DT.mono} fontWeight="700">N₂</SvgText>

      {/* Labels */}
      <LeaderLine x1={x1} y1={y1 - r1} x2={50} y2={50} varName="motor" desc="N₁ (drives)" anchor="start" />
      <LeaderLine x1={x2 + 32} y1={y2 - 14} x2={310} y2={50} varName="fan" desc="N₂ (driven)" anchor="end" />

      <FormulaTag formula="N₂ = N₁ · PD₁ / PD₂   (smaller PD₂ → faster fan)" />
    </DiagramCanvas>
  );
}
